import { useState, useRef, useEffect } from 'react';

const SupportChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Array<{ text: string; isUser: boolean }>>([
    { text: 'Hallo! Ich bin dein LeadSystem-Assistent. Wie kann ich dir helfen?', isUser: false }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isWaitingForEmail, setIsWaitingForEmail] = useState(false);
  const [problemMessage, setProblemMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
  const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

  // Problem-Keywords
  const problemKeywords = [
    'funktioniert nicht', 'fehler', 'problem', 'bug', 'geht nicht', 
    'hilfe', 'error', 'kaputt', 'defekt', 'läuft nicht', 'absturz',
    'hängt', 'langsam', 'lädt nicht', 'zeigt nicht', 'support'
  ];

  // FAQ-Antworten
  const faqResponses: Record<string, string> = {
    'was ist': 'Das LeadSystem ist eine intelligente Plattform zur Lead-Generierung und -Verwaltung. Es hilft dir, potenzielle Kunden zu finden, zu analysieren und zu kontaktieren.',
    'wie funktioniert': 'Das System durchsucht automatisch nach relevanten Leads, analysiert deren Online-Präsenz und hilft dir bei der Kontaktaufnahme. Du kannst Leads filtern, bewerten und verwalten.',
    'leads finden': 'Nutze die Suchfunktion im CRM-Bereich. Du kannst nach Standort, Branche und weiteren Kriterien filtern. Das System analysiert automatisch Websites und Social-Media-Profile.',
    'affiliate': 'Das Affiliate-Programm ermöglicht es Partnern, Leads zu generieren und Provisionen zu verdienen. Partner erhalten einen eigenen Landing-Page-Link.',
    'kosten': 'Für Informationen zu Preisen und Paketen kontaktiere bitte unser Sales-Team unter info@future-now.online',
    'kontakt': 'Du erreichst uns unter info@future-now.online oder nutze das Kontaktformular auf der Website.',
    'anmelden': 'Klicke auf "Anmelden" in der Navigation und nutze deine E-Mail-Adresse. Du erhältst einen Magic-Link zum Login.',
    'passwort': 'Wir nutzen Magic-Link-Login. Du benötigst kein Passwort - klicke einfach auf den Link in deiner E-Mail.',
    'export': 'Du kannst Leads als CSV exportieren. Nutze die Export-Funktion im CRM-Dashboard.',
    'filter': 'Im CRM-Bereich kannst du nach verschiedenen Kriterien filtern: Status, Quelle, Datum, Bewertung und mehr.',
    'analyse': 'Das System analysiert automatisch Websites und Social-Media-Profile von Leads und zeigt dir wichtige Informationen.',
    'template': 'Im Templates-Bereich kannst du E-Mail-Vorlagen erstellen und für die Kontaktaufnahme nutzen.',
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const detectProblem = (text: string): boolean => {
    const lowerText = text.toLowerCase();
    return problemKeywords.some(keyword => lowerText.includes(keyword));
  };

  const extractEmail = (text: string): string | null => {
    const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;
    const match = text.match(emailRegex);
    return match ? match[0] : null;
  };

  const findAnswer = (text: string): string | null => {
    const lowerText = text.toLowerCase();
    for (const [key, answer] of Object.entries(faqResponses)) {
      if (lowerText.includes(key)) {
        return answer;
      }
    }
    return null;
  };

  const sendSupportNotification = async (userEmail: string, message: string) => {
    setIsSending(true);
    try {
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/send-support-notification`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({
            subject: '🚨 Support-Anfrage: Problem im LeadSystem',
            message: message,
            userEmail: userEmail,
            userName: 'CRM-Benutzer',
            timestamp: new Date().toISOString()
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Fehler beim Senden');
      }

      const data = await response.json();

      if (data.success) {
        setMessages(prev => [...prev, {
          text: '✅ Deine Support-Anfrage wurde erfolgreich gesendet! Wir melden uns schnellstmöglich bei dir.',
          isUser: false
        }]);
      } else {
        throw new Error(data.error || 'Unbekannter Fehler');
      }
    } catch (error) {
      console.error('Fehler beim Senden:', error);
      setMessages(prev => [...prev, {
        text: '❌ Entschuldigung, beim Senden ist ein Fehler aufgetreten. Bitte kontaktiere uns direkt unter info@future-now.online',
        isUser: false
      }]);
    } finally {
      setIsSending(false);
      setIsWaitingForEmail(false);
      setProblemMessage('');
    }
  };

  const handleSend = () => {
    if (!inputValue.trim() || isSending) return;

    const userMessage = inputValue.trim();
    setMessages(prev => [...prev, { text: userMessage, isUser: true }]);
    setInputValue('');

    // Wenn wir auf E-Mail warten
    if (isWaitingForEmail) {
      const email = extractEmail(userMessage);
      if (email) {
        sendSupportNotification(email, problemMessage);
      } else {
        setMessages(prev => [...prev, {
          text: 'Bitte gib eine gültige E-Mail-Adresse ein, damit wir dich kontaktieren können.',
          isUser: false
        }]);
      }
      return;
    }

    // Problem erkannt?
    if (detectProblem(userMessage)) {
      setProblemMessage(userMessage);
      setIsWaitingForEmail(true);
      setTimeout(() => {
        setMessages(prev => [...prev, {
          text: 'Das tut mir leid! Ich leite dein Problem an unser Support-Team weiter. Bitte gib mir deine E-Mail-Adresse, damit wir dich kontaktieren können.',
          isUser: false
        }]);
      }, 500);
      return;
    }

    // FAQ-Antwort suchen
    const answer = findAnswer(userMessage);
    setTimeout(() => {
      if (answer) {
        setMessages(prev => [...prev, { text: answer, isUser: false }]);
      } else {
        setMessages(prev => [...prev, {
          text: 'Entschuldigung, dazu habe ich keine passende Antwort. Möchtest du mit unserem Support-Team sprechen? Schreib einfach "Hilfe" oder beschreibe dein Problem.',
          isUser: false
        }]);
      }
    }, 500);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Chat-Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 w-14 h-14 bg-teal-600 hover:bg-teal-700 text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110 z-50"
          aria-label="Support-Chat öffnen"
        >
          <i className="ri-question-answer-line text-2xl"></i>
        </button>
      )}

      {/* Chat-Fenster */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 w-96 h-[600px] bg-white rounded-2xl shadow-2xl flex flex-col z-50 border border-gray-200">
          {/* Header */}
          <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white p-4 rounded-t-2xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <i className="ri-customer-service-2-line text-xl"></i>
              </div>
              <div>
                <h3 className="font-semibold text-base">LeadSystem Support</h3>
                <p className="text-xs text-teal-100">Wir helfen dir gerne</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="w-8 h-8 hover:bg-white/20 rounded-lg flex items-center justify-center transition-colors"
              aria-label="Chat schließen"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.isUser ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] px-4 py-2 rounded-2xl ${
                    msg.isUser
                      ? 'bg-teal-600 text-white rounded-br-sm'
                      : 'bg-white text-gray-800 rounded-bl-sm shadow-sm border border-gray-100'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                </div>
              </div>
            ))}
            {isSending && (
              <div className="flex justify-start">
                <div className="bg-white px-4 py-2 rounded-2xl rounded-bl-sm shadow-sm border border-gray-100">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-teal-600 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-teal-600 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-teal-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-gray-200 bg-white rounded-b-2xl">
            <div className="flex gap-2">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={isWaitingForEmail ? "Deine E-Mail-Adresse..." : "Schreib deine Frage..."}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                disabled={isSending}
              />
              <button
                onClick={handleSend}
                disabled={!inputValue.trim() || isSending}
                className="w-10 h-10 bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 text-white rounded-lg flex items-center justify-center transition-colors whitespace-nowrap"
                aria-label="Nachricht senden"
              >
                <i className="ri-send-plane-fill text-lg"></i>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SupportChatbot;
